import { useEffect, useRef, type ReactNode } from "react";
import type { Canal } from "@/lib/envio-colaborador-api";
import { cn } from "@/lib/utils";

/**
 * Título do passo. Recebe o foco quando o passo (o texto) muda, para o leitor de tela
 * anunciar a tela nova em vez de o foco cair no <body>. `focar={false}` quando o passo
 * já põe o foco num campo (e-mail, código).
 */
export function Titulo({
  children,
  className,
  focar = true,
}: {
  children: string;
  className?: string;
  focar?: boolean;
}) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focar) ref.current?.focus();
  }, [children, focar]);
  return (
    <h1
      ref={ref}
      tabIndex={-1}
      className={cn("leading-tight font-bold outline-none", className)}
    >
      {children}
    </h1>
  );
}

function iniciais(nome: string) {
  return nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

export function Moldura({
  canal,
  passo,
  children,
  acao,
  onVoltar,
  voltarDesabilitado = false,
}: {
  canal: Canal;
  passo: 1 | 2 | null;
  children: ReactNode;
  acao?: ReactNode;
  onVoltar?: () => void;
  voltarDesabilitado?: boolean;
}) {
  return (
    <div className="min-h-dvh bg-white text-slate-950">
      <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col">
        <header className="flex items-center justify-between px-6 pt-5 pb-3">
          <div className="flex items-center gap-2.5">
            {canal.logoUrl ? (
              <img
                src={canal.logoUrl}
                alt=""
                className="h-9 w-9 rounded-lg object-contain"
              />
            ) : (
              <span
                aria-hidden="true"
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-700 text-sm font-bold text-white"
              >
                {iniciais(canal.nome)}
              </span>
            )}
            <span className="text-[15px] font-semibold">{canal.nome}</span>
          </div>
          {passo ? (
            <span className="text-[13px] text-slate-600">
              <span aria-hidden="true">{passo} de 2</span>
              <span className="sr-only">Passo {passo} de 2</span>
            </span>
          ) : (
            <span className="text-[13px] font-semibold text-green-800">
              Concluído
            </span>
          )}
        </header>
        <div className="mx-6 h-1 rounded bg-slate-200" aria-hidden="true">
          <div
            className={`h-1 rounded ${passo ? "bg-blue-700" : "bg-green-600"}`}
            style={{ width: `${((passo ?? 2) / 2) * 100}%` }}
          />
        </div>
        {onVoltar && (
          <button
            type="button"
            aria-label="Voltar"
            className="mx-6 mt-2 -mb-4 flex min-h-11 items-center self-start text-sm font-semibold text-blue-700 disabled:opacity-50"
            onClick={onVoltar}
            disabled={voltarDesabilitado}
          >
            ‹ Voltar
          </button>
        )}
        <main className="flex flex-1 flex-col gap-3 px-6 pt-8 pb-6">
          {children}
        </main>
        <footer className="sticky bottom-0 flex flex-col gap-3 border-t border-slate-100 bg-white px-6 pt-4 pb-7">
          {acao}
          <p className="text-center text-xs text-slate-500">
            Ambiente seguro · Zavy
          </p>
          <p className="text-center text-xs text-slate-500">
            Seus dados são usados só para o RH analisar o atestado.{" "}
            <a
              href="https://zavy.com.br/privacidade"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-700 underline"
            >
              Privacidade
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}
